import { getTokens, } from "@bracket/chainography";
import { multiCaller, contract } from "@bracket/chainography/contracts";
import type { ColateralBalances,} from "@types";
import { wagmiConfig as config, } from "@wagmiconfig";
import { getAggregateError, } from "@bracket/catch";



/**
 * Retrieves and calculates token balances for all platform tokens
 * 
 * @returns {Promise<ColateralBalances[]>} Array of token balances with their respective percentages
 * 
 * @remarks
 * This function:
 * 1. Gets all platform tokens
 * 2. Fetches collateral info for each token
 * 3. Calculates raw balances and counts
 * 4. Sorts tokens with BRKTETH last and by balance amount
 * 5. Calculates percentage distribution across all tokens
 * 6. Returns formatted balance information with percentages
 */
export const getTokenBalancesOld = async (): Promise<ColateralBalances[]> => {

	const tokens = getTokens({ platform_supply: true, });

	const balancesz = await multiCaller<[ bigint, bigint, bigint, ]>(config, tokens.map(tok => {
		return contract("BrktETH", "getCollateralInfo", tok.address);
	}));

	const balances: ColateralBalances[] = await Promise.all(tokens.map(async tok => {
		const response = await getCollateralInfo({
			token: tok,
			config,
		});
		const result = response.totalDeposit;
		return {
			title: tok.symbol,
			value: result,
			count: Number(result) / 1e18,
			symbol: tok.symbol,
			label: (Number(result) / 1e18).toFixed(2),
			completePercentage: 0,
			rate: 0,
		};
	}));

	const sorted = balances.sort((tk1, tk2) => {
		if(tk1.symbol === "BRKTETH") return 1;
		if(tk1.count > tk2.count) return 1;
		return -1;
	})

	const total = sorted.reduce((acc, b) => acc + b.count, 0);

	const result = sorted.map(B => {
		B.completePercentage = (B.count / total) * 100;
		if(Number.isNaN(B.completePercentage))
			B.completePercentage = 0;
		B.label += ` (${B.completePercentage}%)`;
		return B;
	});

	return result;

};


export const getTokenBalances = async (): Promise<ColateralBalances[]> => {

	try {

		const tokens = getTokens({ platform_supply: true, });

		const [ results, errors, ] = await multiCaller<Array<{ collateral: `0x${string}`; totalDeposit: bigint; whitelisted: boolean; }>>(config, tokens.map(tok => {
			return contract("BrktETH", "getCollateralInfo", tok.address);
		}));

		if(errors) {
			throw getAggregateError(errors, `failed getting token balances`);
		}

		const balances = tokens.map((tok, idx) => {
			const { totalDeposit, } = results[idx];
			return {
				title: tok.symbol,
				value: totalDeposit,
				count: Number(totalDeposit) / 1e18,
				symbol: tok.symbol,
				label: (Number(totalDeposit) / 1e18).toFixed(2),
				completePercentage: 0,
				rate: -1,
			};
		});

		const sorted = balances.sort((tk1, tk2) => {
			if(tk1.symbol === "BRKTETH") return 1;
			if(tk1.count > tk2.count) return 1;
			return -1;
		});

		const total = sorted.reduce((acc, b) => acc + b.count, 0);

		const result = sorted.map(B => {
			B.completePercentage = (B.count / total) * 100;
			if(Number.isNaN(B.completePercentage))
				B.completePercentage = 0;
			B.label += ` (${B.completePercentage}%)`;
			return B;
		})

		return result;
	}
	catch(err) {

		throw getAggregateError(err, `failed getting token balances`);
	}

};
